import type { BotExamLangTranslationQuestionViewModel } from '@/shared/api/generated/types/BotExamLangTranslationQuestionViewModel';
import type { BotExamReviewQuestionViewModel } from '@/shared/api/generated/types/BotExamReviewQuestionViewModel';
import type { RevisionQuestion } from '../types/revision';
import { mapQuestionType } from './questionType';

/**
 * Converte uma questão de revisão (`BotExamReviewQuestionViewModel`) para o
 * modelo de exibição `RevisionQuestion` da tela de revisão.
 *
 * O campo de UI `retranslateWithAI` é a apresentação do campo `shouldTranslate`
 * do DTO — não existe estado local sem correspondência no contrato da API.
 *
 * @param dto - Questão de revisão retornada pela API
 * @param index - Índice (1-based) da questão na lista
 * @returns Questão no formato de exibição da tela de revisão
 */
export function toRevisionQuestion(
  dto: BotExamReviewQuestionViewModel,
  index: number,
): RevisionQuestion {
  return {
    id: dto.id ?? index,
    index,
    label: dto.label ?? '',
    lessonExcerpt: dto.transcriptExcerpt ?? '',
    explanation: dto.explanation ?? '',
    questionType: mapQuestionType(dto.type),
    isReviewed: dto.isReviewed ?? false,
    retranslateWithAI: dto.shouldTranslate ?? false,
    alternatives: (dto.options ?? []).map((option, optionIndex) => ({
      id: option.id ?? optionIndex,
      text: option.label ?? '',
      isCorrect: option.isCorrect ?? false,
    })),
  };
}

/**
 * Aplica as edições de UI (`Partial<RevisionQuestion>`) sobre a questão original
 * do DTO, preservando os campos não editados (`type`, ids de opções, etc.). Usado
 * para reconstruir o `ClassroomDisciplineBotExamReviewViewModel` no `POST /review`.
 *
 * O toggle `retranslateWithAI` é enviado de volta no campo `shouldTranslate`.
 *
 * @param dto - Questão original carregada do modelo de revisão
 * @param patch - Edições locais do operador (pode ser `undefined`)
 * @returns Questão do DTO com as edições aplicadas
 */
export function applyReviewEdits(
  dto: BotExamReviewQuestionViewModel,
  patch: Partial<RevisionQuestion> | undefined,
): BotExamReviewQuestionViewModel {
  if (!patch) return dto;
  return {
    ...dto,
    ...(patch.label !== undefined ? { label: patch.label } : {}),
    ...(patch.explanation !== undefined ? { explanation: patch.explanation } : {}),
    ...(patch.lessonExcerpt !== undefined ? { transcriptExcerpt: patch.lessonExcerpt } : {}),
    ...(patch.isReviewed !== undefined ? { isReviewed: patch.isReviewed } : {}),
    ...(patch.retranslateWithAI !== undefined ? { shouldTranslate: patch.retranslateWithAI } : {}),
    ...(patch.alternatives
      ? {
          options: patch.alternatives.map(alt => ({
            id: alt.id,
            label: alt.text,
            isCorrect: alt.isCorrect,
          })),
        }
      : {}),
  };
}

/**
 * Monta o payload de persistência individual (`BotExamLangTranslationQuestionViewModel`)
 * de uma questão da revisão a partir do DTO original e das edições locais, para o
 * `PUT /api/v1/exam/revision/question` (endpoint equivalente de questão individual).
 *
 * @param dto - Questão original carregada do modelo de revisão
 * @param patch - Edições locais do operador (pode ser `undefined`)
 * @param botExamLangId - ID do idioma da prova (pt-BR) ao qual a questão pertence
 * @returns Payload pronto para o `PUT /question`
 */
export function toReviewQuestionSavePayload(
  dto: BotExamReviewQuestionViewModel,
  patch: Partial<RevisionQuestion> | undefined,
  botExamLangId: number,
): BotExamLangTranslationQuestionViewModel {
  const edited = applyReviewEdits(dto, patch);
  return {
    classroomDisciplineBotExamLangId: botExamLangId,
    ...(edited.id !== undefined ? { questionId: edited.id } : {}),
    ...(edited.type !== undefined ? { type: edited.type } : {}),
    ...(edited.label !== undefined ? { label: edited.label } : {}),
    ...(edited.explanation !== undefined ? { explanation: edited.explanation } : {}),
    ...(edited.transcriptExcerpt !== undefined
      ? { transcriptExcerpt: edited.transcriptExcerpt }
      : {}),
    ...(edited.isReviewed !== undefined ? { isReviewed: edited.isReviewed } : {}),
    options: (edited.options ?? []).map(option => ({
      ...(option.id !== undefined ? { id: option.id } : {}),
      ...(option.label !== undefined ? { label: option.label } : {}),
      ...(option.isCorrect !== undefined ? { isCorrect: option.isCorrect } : {}),
    })),
  };
}
